import React, { useState, useEffect } from 'react'
import { FormGroup, Label, Button } from 'reactstrap'
import { getInstructorStudent } from '../../lib/instructorstudentview'
import { getInstructorStudent as getInstructorData, getStudentIdData } from '../../lib/instructorstudentnotesview'
import { deleteIdData } from '../../lib/studentnotes'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { DataManagementGrid } from '../Common/DataManagementGrid'
import { notesColumns } from '../../Data/NotesManagement/NotesListData'
import NoteModal from './NoteModal'

const InstructorNotesList = () => {
	const [notes, setNotes] = useState([])
	const [selectedStudent, setSelectedStudent] = useState('All')
	const [instructorStudent, setInstructorStudent] = useState([])
	const [searchQuery, setSearchQuery] = useState('')
	const [loading, setLoading] = useState(true)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [editingNote, setEditingNote] = useState(null)
	const [isDarkMode, setIsDarkMode] = useState(false)

	const { user } = useAuth()

	const fetchData = async () => {
		if (user?.id) {
			if (selectedStudent !== 'All') {
				getStudentData(selectedStudent)
			} else {
				getAllData()
			}
		}
	}

	const getAllData = async () => {
		try {
			setLoading(true)
			const data = await getInstructorData(user.id)
			setNotes(data)
		} catch (error) {
			console.error('Error fetching notes data:', error)
		} finally {
			setLoading(false)
		}
	}

	const getStudentData = async (studentId) => {
		try {
			setLoading(true)
			const data = await getStudentIdData(studentId)
			setNotes(data)
		} catch (error) {
			console.error('Error fetching student notes data:', error)
		} finally {
			setLoading(false)
		}
	}

	const getInstructorStudentData = async () => {
		try {
			const students = await getInstructorStudent(user.id)
			setInstructorStudent(students)
		} catch (error) {
			console.error('Error fetching instructor students:', error)
		}
	}

	const handleDelete = async (id) => {
		const choice = window.confirm('Are you sure you want to delete?')
		if (choice) {
			try {
				await deleteIdData(id)
				fetchData()
			} catch (error) {
				console.error('Error deleting note:', error)
			}
		}
	}

	const handleEdit = (note) => {
		setEditingNote(note)
		setIsModalOpen(true)
	}

	const handleAdd = () => {
		setEditingNote(null)
		setIsModalOpen(true)
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen)
	}

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	// Custom header component with student selector
	const customHeaderComponent = (
		<div className="d-flex align-items-center gap-3">
			<div style={{ width: '300px' }}>
				<FormGroup className="mb-0">
					<Label className="form-control-label">Students</Label>
					<select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} className="form-control">
						<option value="All">All</option>
						{instructorStudent.map((data, ix) => (
							<option key={ix} value={data.studentId}>
								{data.name + ' ' + data.surname}
							</option>
						))}
					</select>
				</FormGroup>
			</div>
		</div>
	)

	useEffect(() => {
		if (user?.id) {
			getInstructorStudentData()
		}
	}, [user?.id])

	useEffect(() => {
		fetchData()
	}, [selectedStudent, user?.id])

	// Add onDelete function to each note for the DataManagementGrid
	const notesWithDelete = notes.map((note) => ({
		...note,
		onDelete: () => handleDelete(note.id)
	}))

	return (
		<>
			<DataManagementGrid
				title="Notes"
				subTitle="Manage student notes"
				breadcrumbTitle="Notes"
				breadcrumbMainTitle="Notes Management"
				columns={(layout, role) => notesColumns(layout, role, handleEdit)}
				data={notesWithDelete}
				loading={loading}
				searchQuery={searchQuery}
				setSearchQuery={setSearchQuery}
				searchPlaceholder="Search notes..."
				createButtonText="Add New Note"
				additionalActionButton={
					<Button onClick={handleAdd} color="primary" disabled={selectedStudent === 'All'}>
						<i className="fa fa-plus me-2"></i>
						Add New Note
					</Button>
				}
				onDelete={handleDelete}
				searchFields={['title', 'name', 'surname']}
				noDataMessage="No notes found"
				customHeaderComponent={customHeaderComponent}
			/>
			<NoteModal
				isOpen={isModalOpen}
				toggle={toggleModal}
				instructorId={user?.id}
				studentId={selectedStudent}
				editingNote={editingNote}
				onSave={fetchData}
				isDarkMode={isDarkMode}
			/>
		</>
	)
}

export { InstructorNotesList }
